import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { DuplicateReasonBridge } from "../../../components/team/DuplicateEmployees/DuplicateReasonBridge";
import { DuplicateRecordCell } from "../../../components/team/DuplicateEmployees/DuplicateRecordCell";
import { STATIC_PAIR_COLUMN } from "../../../components/team/DuplicateEmployees/duplicatePairs";
import { MergePairButton, mergePairLabels } from "../../../components/team/DuplicateEmployees/MergePairButton";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import type { SystemGridColDef } from "../../../components/UIToolkit/Tables/SystemDataGrid/types";
import { DUPLICATE_PAIR, ROSTER_MERGE_GRID } from "../../../theme/tokens";
import type { SuspicionReason } from "../../../utils/nurseMergeModels";
import type { RosterMatchedOn } from "../hooks";
import type { FloatPoolMergeRow, RowMergeOutcome } from "./floatPoolMerge";

type Options = {
	dataTestId: string;
	outcomes: ReadonlyMap<string, RowMergeOutcome>;
	onReview: (row: FloatPoolMergeRow) => void;
	/** The department's name-order flag, for the pair's names on the merge button. */
	lastNameFirst: boolean;
};

/** A roster match drawn with the Team tab's reasons, so both merge screens explain a pair the same way. */
const MATCH_REASON: Record<RosterMatchedOn, SuspicionReason> = { email: "same_email", phone_number: "same_phone", name: "same_name" };

/** The row's action: merge the pair, or what an earlier merge already did to it. */
const actionCell = (
	row: FloatPoolMergeRow,
	outcome: RowMergeOutcome | undefined,
	t: TFunction,
	{ dataTestId, onReview, lastNameFirst }: Omit<Options, "outcomes">,
) => {
	if (outcome?.kind === "merged") {
		return <MetaChip tone="success" label={t("onboarding_load.float_pool.merged")} dataTestId={`${dataTestId}-float-pool-merged-${row.key}`} />;
	}
	if (outcome?.kind === "superseded") {
		// Visible, not a tooltip: a read-only chip never takes focus, so a hover hint would be unreachable by keyboard and touch.
		return (
			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
				<MetaChip label={t("onboarding_load.float_pool.superseded")} dataTestId={`${dataTestId}-float-pool-superseded-${row.key}`} />
				<Typography variant="caption" color="text.secondary" data-testid={`${dataTestId}-float-pool-superseded-caption-${row.key}`}>
					{t("onboarding_load.float_pool.superseded_caption", { removedId: outcome.removedId, keptId: outcome.keptId })}
				</Typography>
			</Box>
		);
	}
	return (
		<MergePairButton
			labels={mergePairLabels(row.pair.nurse, row.pair.pairedNurse, lastNameFirst)}
			onClick={() => onReview(row)}
			dataTestId={`${dataTestId}-float-pool-review-${row.key}`}
		/>
	);
};

export const buildFloatPoolMatchColumns = (t: TFunction, { dataTestId, outcomes, onReview, lastNameFirst }: Options): SystemGridColDef<FloatPoolMergeRow>[] => [
	{
		...STATIC_PAIR_COLUMN,
		field: "recordHere",
		headerName: t("onboarding_load.float_pool.column.record_here"),
		flex: 1,
		minWidth: ROSTER_MERGE_GRID.RECORD_MIN_WIDTH,
		renderCell: ({ row }) => <DuplicateRecordCell record={row.recordHere} matched={row.matched} dataTestId={`${dataTestId}-float-pool-here-${row.key}`} />,
	},
	{
		...STATIC_PAIR_COLUMN,
		field: "matchedOn",
		headerName: t("onboarding_load.float_pool.column.matched_on"),
		headerAlign: "center",
		width: ROSTER_MERGE_GRID.BRIDGE_COLUMN_WIDTH,
		renderCell: ({ row }) => (
			<DuplicateReasonBridge reasons={row.matchedOn.map((field) => MATCH_REASON[field])} dataTestId={`${dataTestId}-float-pool-reasons-${row.key}`} />
		),
	},
	{
		...STATIC_PAIR_COLUMN,
		field: "existing",
		headerName: t("onboarding_load.float_pool.column.existing"),
		flex: 1,
		minWidth: ROSTER_MERGE_GRID.RECORD_MIN_WIDTH,
		renderCell: ({ row }) => <DuplicateRecordCell record={row.existing} matched={row.matched} dataTestId={`${dataTestId}-float-pool-existing-${row.key}`} />,
	},
	{
		...STATIC_PAIR_COLUMN,
		field: "actions",
		headerName: t("onboarding_load.float_pool.column.action"),
		width: DUPLICATE_PAIR.ACTIONS_COLUMN_WIDTH,
		renderCell: ({ row }) => actionCell(row, outcomes.get(row.key), t, { dataTestId, onReview, lastNameFirst }),
	},
];
